import React, { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { useAuth } from '../shared/hooks/useApi/useAuth';
import { useAuthTokenStore } from '../shared/store/authStore';
import AdminAnalyticsDashboard from './analytics/component/AdminAnalyticsDashboard';
import AdminOwnerAnalyticsDashboard from './analytics/component/AdminOwnerAnalyticsDashboard';
import { useAdminAnalytics } from './analytics/hook/useAdminAnalytics';
import { useAdminAnalyticsStore } from './analytics/store/adminAnalyticsStore';

const ANALYTICS_ZONE = 'Asia/Seoul';
type AnalyticsView = 'external' | 'owner';

interface DateRange {
  from: string;
  to: string;
}

const getLatestCompletedDate = (): string =>
  DateTime.now().setZone(ANALYTICS_ZONE).minus({ days: 1 }).toISODate() ?? '';

const getDateBefore = (date: string, days: number): string =>
  DateTime.fromISO(date, { zone: ANALYTICS_ZONE })
    .minus({ days })
    .toISODate() ?? '';

const createDateRange = (to: string, days: number): DateRange => ({
  from: getDateBefore(to, days - 1),
  to,
});

const AdminAnalytics: React.FC = () => {
  const latestCompletedDate = getLatestCompletedDate();
  const [activeView, setActiveView] =
    useState<AnalyticsView>('external');
  const [externalRange, setExternalRange] = useState<DateRange>(() =>
    createDateRange(latestCompletedDate, 30),
  );
  const [ownerRange, setOwnerRange] = useState<DateRange>(() =>
    createDateRange(latestCompletedDate, 28),
  );
  const { user } = useAuth();
  const authToken = useAuthTokenStore((state) => state.authToken);
  const externalData = useAdminAnalyticsStore((state) => state.externalData);
  const ownerData = useAdminAnalyticsStore((state) => state.ownerData);
  const { loadExternalDaily, loadOwnerDaily } = useAdminAnalytics();
  const loadExternalDailyRef = useRef(loadExternalDaily);
  const loadOwnerDailyRef = useRef(loadOwnerDaily);
  loadExternalDailyRef.current = loadExternalDaily;
  loadOwnerDailyRef.current = loadOwnerDaily;

  useEffect(() => {
    if (!user || !authToken) return;

    if (activeView === 'external') {
      void loadExternalDailyRef.current(
        externalRange.from,
        externalRange.to,
      );
      return;
    }

    void loadOwnerDailyRef.current(ownerRange.from, ownerRange.to);
    // 인증 복구와 탭 진입 시 현재 범위를 새로 조회한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authToken, activeView]);

  const range = activeView === 'external' ? externalRange : ownerRange;
  const activeData = activeView === 'external' ? externalData : ownerData;

  const setRange = (nextRange: DateRange): void => {
    if (activeView === 'external') {
      setExternalRange(nextRange);
      return;
    }
    setOwnerRange(nextRange);
  };

  const load = (
    view: AnalyticsView = activeView,
    nextFrom = range.from,
    nextTo = range.to,
  ): void => {
    if (!nextFrom || !nextTo || nextFrom > nextTo) return;
    if (view === 'external') {
      void loadExternalDaily(nextFrom, nextTo);
      return;
    }
    void loadOwnerDaily(nextFrom, nextTo);
  };

  const applyPreset = (days: number): void => {
    const nextTo = latestCompletedDate;
    const nextFrom = getDateBefore(nextTo, days - 1);
    setRange({ from: nextFrom, to: nextTo });
    load(activeView, nextFrom, nextTo);
  };

  const selectView = (view: AnalyticsView): void => {
    if (view === activeView) return;
    setActiveView(view);
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>Analytics</Title>
          <Description>
            {activeView === 'external'
              ? '소유자를 제외한 공개 아카이브 방문'
              : '로그인 소유자의 자기 아카이브 방문'}{' '}
            · {ANALYTICS_ZONE} 기준
          </Description>
        </div>
        <Filters>
          <PresetButton type="button" onClick={() => applyPreset(7)}>
            7일
          </PresetButton>
          <PresetButton
            type="button"
            onClick={() => applyPreset(activeView === 'external' ? 30 : 28)}
          >
            {activeView === 'external' ? '30일' : '28일'}
          </PresetButton>
          <DateInput
            type="date"
            value={range.from}
            max={range.to}
            onChange={(event) =>
              setRange({ ...range, from: event.target.value })
            }
          />
          <span>–</span>
          <DateInput
            type="date"
            value={range.to}
            min={range.from}
            max={latestCompletedDate}
            onChange={(event) =>
              setRange({ ...range, to: event.target.value })
            }
          />
          <LoadButton
            type="button"
            disabled={!range.from || !range.to || range.from > range.to}
            onClick={() => load()}
          >
            조회
          </LoadButton>
        </Filters>
      </Header>

      <Tabs role="tablist" aria-label="분석 지표">
        <TabButton
          type="button"
          role="tab"
          aria-selected={activeView === 'external'}
          $active={activeView === 'external'}
          onClick={() => selectView('external')}
        >
          외부 도달
        </TabButton>
        <TabButton
          type="button"
          role="tab"
          aria-selected={activeView === 'owner'}
          $active={activeView === 'owner'}
          onClick={() => selectView('owner')}
        >
          소유자 활동
        </TabButton>
      </Tabs>

      {activeView === 'external' && externalData && (
        <AdminAnalyticsDashboard data={externalData} />
      )}
      {activeView === 'owner' && ownerData && (
        <AdminOwnerAnalyticsDashboard data={ownerData} />
      )}

      <Footer>
        마지막 집계:{' '}
        {activeData?.lastAggregatedAt
          ? DateTime.fromISO(activeData.lastAggregatedAt)
              .setZone(ANALYTICS_ZONE)
              .toFormat('yyyy-LL-dd HH:mm')
          : '집계 전'}
      </Footer>
    </Container>
  );
};

export default AdminAnalytics;

const Container = styled.main`
  width: 96%;
  height: calc(100vh - 12.8vh);
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 7vh;
  margin-bottom: 5.8vh;
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const Title = styled.h1`
  margin: 0;
  ${({ theme }) => theme.typography.Body_00_1};
`;

const Description = styled.p`
  margin: 4px 0 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const Tabs = styled.div`
  display: flex;
  gap: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 0 0 8px;
  border: none;
  border-bottom: 2px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.color_Gray_02 : 'transparent'};
  background: transparent;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.color_Gray_02 : theme.colors.color_Gray_04};
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const PresetButton = styled.button`
  padding: 5px 8px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
`;

const DateInput = styled.input`
  padding: 5px 6px;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  background: transparent;
  outline: none;
  ${({ theme }) => theme.typography.Body_04};
`;

const LoadButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_02};
  background: ${({ theme }) => theme.colors.color_Gray_02};
  color: ${({ theme }) => theme.colors.color_White};
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const Footer = styled.footer`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Footer_01};
`;
